import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { forEach, get, isUndefined, uniq } from 'lodash';
import PropTypes from 'prop-types';
import useForm from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import MemberForm from '../../FormComponents/MemberForm';
import PersonCard from '../../PersonCard';
import SuggestedPersons from '../../SuggestedPersons';
import ScrollToTopPage from '../../ScrollToTopPage';
import TitleTypography from '../../MUI/TitleTypography';
import Navigation from './Navigation';
import useScrollToFormErrorElement from '../../../hooks/useScrollToFormErrorElement';
import usePrevious from '../../../hooks/usePrevious';
import { contractMembersSchema, newMemberSchema } from '../../../validations/userFormValidations';
import { uniqueSocCardValidator, uniquePassportValidator } from '../../../validations/uniqueItemsValidator';
import { getMembersOtherThanSigner, getContractMemberItems } from '../../../helpers/contractHelpers';
import { COUNTRIES } from '../../../constants';

import signerIcon from '../../../img/svgs/sign.svg';
import styles from './styles.module.scss';
import commonStyles from '../../../containers/Registration/userFormCommonStyles.module.scss';
import useStyles from '../makeStyles';

const { memberStep, suggestedPersonsWrapper, formContainer } = styles;

const getInvalidMemberIds = (members, schema) => {
  let validationError;
  try {
    schema.validateSync({ members }, { abortEarly: false });
  } catch (e) {
    validationError = e;
  }
  const result = [];
  if (!validationError) {
    return [];
  }
  forEach(validationError.inner, (error) => {
    const index = error.path.match(/\[\d+]/)[0];
    const { memberId } = get(members, index);
    result.push(memberId);
  });
  return uniq(result);
};

const scrollTo = (elem) => {
  if (elem) {
    window.scrollTo(0, elem.offsetTop);
  }
};

const MemberStep = ({
  goPrevious,
  goNext,
  blockNextSteps,
  addNewMemberButtonComponent,
  passportIdInfoComponent,
  params,
  step,
  setUnlockedSteps,
  unlockedSteps,
}) => {
  const {
    type: contractType,
    data: contractData,
    memberType,
    members,
    memberBackendErrors,
    suggestionsList,
    memberExistsIcon,
    memberIcon,
    saveMember,
    addMember,
    removeMember,
    removeMemberBackendErrors,
    setHasAddedSigner,
    handleActionsBeforeNext,
    attention,
    hasLanguageSensitiveNames,
    hasAddressInputsLanguageSensitiveNames,
    newMemberValidationSchema = newMemberSchema,
    contractMembersValidationSchema = contractMembersSchema,
    totalPrice,
    handleOpenMobileSummary,
    isDeviceLgSize,
    setStepNavigationBox,
    isMemberFormOpen,
    setIsMemberFormOpen,
    disabledHasAddedSigner,
    handleResolveSpecificData,
    handleWhenHasNoSigner,
  } = params;

  const {
    register,
    setValue,
    errors,
    triggerValidation,
    clearError,
    getValues,
    reset,
    unregister,
    watch,
    handleSubmit,
    formState,
    setError,
  } = useForm({
    validationSchema: newMemberValidationSchema,
    mode: 'onBlur',
  });

  const { signer, hasAddedSigner } = contractData;
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const formRef = useRef();

  const defaultMember = {
    firstName: '',
    lastName: '',
    firstNameEn: '',
    lastNameEn: '',
    citizenship: COUNTRIES.ARM,
    isSaved: false,
    resident: 1,
  };

  const classes = useStyles();
  const membersOtherThanSigner = useMemo(() => getMembersOtherThanSigner(members), [
    members,
  ]);

  const showMemberExistsIcon = useMemo(() => !membersOtherThanSigner.length && memberExistsIcon, [
    memberExistsIcon, membersOtherThanSigner,
  ]);

  const [selectedMember, setSelectedMember] = useState(defaultMember);
  const [newMemberIds, setNewMemberIds] = useState([]);
  const [invalidMemberIds, setInvalidMemberIds] = useState([]);
  const [allMembersValid, setAllMembersValid] = useState(false);
  const [isFormValid, setIsFormValid] = useState(true);
  const [submitCount, setSubmitCount] = useState(0);
  const [hasCanceledForm, setHasCanceledForm] = useState(false);

  const previousCount = usePrevious(submitCount);

  const [memberErrors, setMemberErrors] = useState([]);
  const isInList = Boolean(members.find(({ memberId }) => memberId === signer.memberId));

  const addNewMember = useCallback(
    (member) => {
      const memberWithId = { ...member };
      // NOTE that memberId for signer is 0, so checking for null or undefined here
      if (member.memberId == null) {
        if (members.length === 1 && members[0].memberId !== 1) {
          memberWithId.memberId = 1;
        } else if (members.length) {
          memberWithId.memberId = members.length + 1;
        }
        if (!members.length) {
          memberWithId.memberId = 1;
        }
      }
      dispatch(addMember(memberWithId));
      return memberWithId;
    },
    [addMember, dispatch, members],
  );

  const addEmptyMember = useCallback(
    (data) => {
      const newEmptyMember = addNewMember({ ...defaultMember, ...data });
      setSelectedMember(newEmptyMember);
      setNewMemberIds((current) => ([...current, newEmptyMember.memberId]));
    },
    [addNewMember, defaultMember],
  );

  const removeMemberFromList = useCallback(
    (member) => {
      if (member.memberId === signer.memberId) {
        dispatch(setHasAddedSigner(false));
      }
      dispatch(removeMember(member));
      setSelectedMember((current) => (current.memberId === member.memberId
        ? defaultMember : current));
      setUnlockedSteps(unlockedSteps);
      setHasCanceledForm(true);
    },
    [
      defaultMember,
      dispatch,
      removeMember,
      setHasAddedSigner,
      setUnlockedSteps,
      signer.memberId,
      unlockedSteps,
    ],
  );

  const cancelMemberFrom = useCallback(
    (member) => {
      if (member.memberId === signer.memberId) {
        dispatch(setHasAddedSigner(false));
      }
      setSelectedMember(defaultMember);
      setUnlockedSteps(unlockedSteps);
      setHasCanceledForm(true);
    },
    [signer.memberId, defaultMember, unlockedSteps, setUnlockedSteps, dispatch, setHasAddedSigner],
  );

  const saveMemberToList = useCallback(
    (member) => {
      setSelectedMember((current) => (
        current.memberId === member.memberId ? defaultMember : current));
    },
    [defaultMember],
  );

  const handleAddNewMember = useCallback((data) => {
    if (contractMembersValidationSchema.isValidSync({ members })) {
      addEmptyMember(data);
      scrollTo(formRef.current);
      setIsMemberFormOpen(true);
      clearError();
      setHasCanceledForm(false);
    }
  }, [addEmptyMember, contractMembersValidationSchema, members, setIsMemberFormOpen, clearError]);

  const addSignerToList = useCallback(() => {
    addNewMember({ ...signer, isSigner: true });
  }, [addNewMember, signer]);

  const removeSignerFromList = useCallback(() => {
    const signerWithId = members.find(
      (member) => member.memberId === signer.memberId,
    );
    removeMemberFromList(signerWithId);
  }, [removeMemberFromList, signer, members]);

  useEffect(() => {
    setSubmitCount(formState.submitCount);
  }, [formState.submitCount]);

  useScrollToFormErrorElement(errors, submitCount, previousCount);

  useEffect(() => {
    if (!hasAddedSigner && !membersOtherThanSigner.length && !hasCanceledForm) {
      handleWhenHasNoSigner(() => {
        handleAddNewMember({});
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, hasCanceledForm, membersOtherThanSigner.length, hasAddedSigner]);

  useEffect(() => {
    const emptyMember = members.find((member) => !member.isSaved && member.memberId !== 0);
    if (emptyMember) {
      removeMemberFromList(emptyMember);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [location]);

  useEffect(() => () => {
    setIsMemberFormOpen(false);
  }, [setIsMemberFormOpen]);

  useEffect(() => {
    if (hasAddedSigner) {
      if (!isInList) {
        addSignerToList();
      }
    } else if (isInList) {
      removeSignerFromList();
    }
  }, [addSignerToList, hasAddedSigner, isInList, removeSignerFromList]);

  useEffect(() => {
    uniquePassportValidator.setItems(getContractMemberItems('passport', members, selectedMember.memberId));
  }, [members, selectedMember]);

  useEffect(() => {
    uniqueSocCardValidator.setItems(getContractMemberItems('socCard', members, selectedMember.memberId));
  }, [members, selectedMember]);

  useEffect(() => {
    const areAllMembersValid = contractMembersValidationSchema.isValidSync({ members });
    setAllMembersValid(areAllMembersValid);
    if (areAllMembersValid) {
      setInvalidMemberIds([]);
      setIsFormValid(true);
    } else {
      blockNextSteps();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, blockNextSteps]);

  useEffect(() => {
    if (isMemberFormOpen) {
      blockNextSteps();
    }
  }, [isMemberFormOpen, blockNextSteps, step]);

  useEffect(() => {
    clearError();
  }, [clearError, i18n.language]);

  const handleFormSave = useCallback(
    (e, callback) => {
      scrollTo(formRef.current);
      if (!members.length) {
        setIsFormValid(false);
      }
      if (selectedMember.memberId) {
        // trigger useForm validation if form is open
        const validateAndSubmit = handleSubmit(() => {
        });
        if (validateAndSubmit) {
          validateAndSubmit(e);
        }
      }

      if (allMembersValid && members.length) {
        callback();
      } else {
        const invalidMembers = getInvalidMemberIds(members, contractMembersValidationSchema);
        setInvalidMemberIds(invalidMembers);
        setIsFormValid(false);
        contractMembersValidationSchema
          .validate({ members })
          .then(() => {
          })
          .catch((err) => setMemberErrors(err.errors));
      }
      setUnlockedSteps(unlockedSteps);
    },
    [
      allMembersValid,
      contractMembersValidationSchema,
      handleSubmit,
      members,
      selectedMember.memberId,
      setUnlockedSteps,
      unlockedSteps,
    ],
  );

  useEffect(() => {
    if (allMembersValid) {
      setMemberErrors([]);
    }
  }, [allMembersValid]);

  const editHandler = (member) => {
    // eslint-disable-next-line no-param-reassign
    member.isSaved = true;
    // before selecting another member check if current has any backend error
    // and remove them
    if (selectedMember && memberBackendErrors
      && memberBackendErrors[selectedMember.memberId]) {
      dispatch(removeMemberBackendErrors(selectedMember.memberId));
    }
    setSelectedMember(member);
    setIsMemberFormOpen(true);
    scrollTo(formRef.current);
  };

  const isInvalid = (memberId) => invalidMemberIds.includes(memberId)
    || Boolean(memberBackendErrors && memberBackendErrors[memberId]);

  return (
    <div className={memberStep}>
      <ScrollToTopPage withDelay />
      <Navigation
        setStepNavigationBox={setStepNavigationBox}
        isDeviceLgSize={isDeviceLgSize}
        handleOpenMobileSummary={handleOpenMobileSummary}
        totalPrice={totalPrice}
        goPrevious={goPrevious}
        handleActionsBeforeNext={handleActionsBeforeNext}
        handleFormSave={handleFormSave}
        isFormValid={isFormValid}
        isMemberFormOpen={isMemberFormOpen}
        members={members}
        allMembersValid={allMembersValid}
        goNext={goNext}
      />
      <PersonCard
        personIcon={signerIcon}
        contractType={contractType}
        person={signer}
        isSigner
        hasAddedSigner={hasAddedSigner}
        setHasAddedSigner={(value) => {
          dispatch(setHasAddedSigner(value));
          if (!value && !membersOtherThanSigner.length) {
            handleWhenHasNoSigner(() => {
              handleAddNewMember({});
            });
          }
        }}
        goPrevious={goPrevious}
        editHandler={goPrevious}
        disabledHasAddedSigner={disabledHasAddedSigner}
      />
      {
        !!memberErrors.length && (
          <div className={styles.errorContainer}>
            <span className={styles.errorMsg}>{t(`${memberErrors[0]}`)}</span>
          </div>
        )
      }
      {!!suggestionsList && !!suggestionsList.length && (
        <div className={suggestedPersonsWrapper}>
          <SuggestedPersons
            contractType={contractType}
            memberList={members}
            suggestedList={suggestionsList}
            onAdd={addNewMember}
            onRemove={removeMemberFromList}
          />
        </div>
      )}
      <div>
        {!!membersOtherThanSigner.length && !isMemberFormOpen && (
          <TitleTypography className={classes.sectionTitle}>
            {t(`${contractType}.otherMembers`)}
          </TitleTypography>
        )}

        {membersOtherThanSigner
          .filter((member) => member.memberId !== selectedMember.memberId)
          .map((member, index) => (
            <PersonCard
              key={member.memberId}
              disabledCard={selectedMember.memberId
                ? (member.memberId !== selectedMember.memberId)
                : false}
              index={hasAddedSigner ? index + 2 : index + 1}
              contractType={contractType}
              person={member}
              editHandler={editHandler}
              removeHandler={removeMemberFromList}
              isInvalid={isInvalid(member.memberId)}
              personIcon={memberIcon}
              goPrevious={goPrevious}
            />
          ))}

        {showMemberExistsIcon && (
          <img alt="" className={classes.memberExistsIcon} src={memberExistsIcon} />
        )}
        {
          !isMemberFormOpen && addNewMemberButtonComponent && (
            React.cloneElement(addNewMemberButtonComponent, {
              disabled: !allMembersValid,
              isDeviceLgSize,
              handleAddNewMember,
              setValue,
              register,
            })
          )
        }
        <span ref={formRef} />
        {!isUndefined(selectedMember.memberId) && (
          <>
            <MemberForm
              contractType={contractType}
              memberType={memberType}
              member={selectedMember}
              memberBackendErrors={memberBackendErrors}
              saveMember={saveMember}
              cancelHandler={cancelMemberFrom}
              saveHandler={saveMemberToList}
              formProps={{
                register,
                setValue,
                errors,
                triggerValidation,
                clearError,
                getValues,
                reset,
                unregister,
                watch,
                setError,
                handleSubmit,
              }}
              hasLanguageSensitiveNames={hasLanguageSensitiveNames}
              hasAddressInputsLanguageSensitiveNames={hasAddressInputsLanguageSensitiveNames}
              isNewMember={newMemberIds.includes(selectedMember.memberId)}
              setIsFormOpen={setIsMemberFormOpen}
              handleFormSave={handleFormSave}
              handleResolveSpecificData={handleResolveSpecificData}
              passportIdInfoComponent={passportIdInfoComponent}
              styles={{
                formContainer,
                commonStyles,
                classes,
                moduleStyles: styles,
              }}
              newMemberValidationSchema={newMemberValidationSchema}
              removeMemberFromList={removeMemberFromList}
            />
          </>
        )}
      </div>
      {attention && (
        <div className={styles.description}>
          <div>
            <div dangerouslySetInnerHTML={{ __html: t(`${attention}`) }} />
          </div>
        </div>
      )}
    </div>
  );
};

MemberStep.propTypes = {
  goPrevious: PropTypes.func.isRequired,
  goNext: PropTypes.func.isRequired,
  blockNextSteps: PropTypes.func.isRequired,
  addNewMemberButtonComponent: PropTypes.element,
  passportIdInfoComponent: PropTypes.element,
  params: PropTypes.shape({
    type: PropTypes.string.isRequired,
    data: PropTypes.shape({
      signer: PropTypes.shape({
        memberId: PropTypes.number,
      }),
      hasAddedSigner: PropTypes.bool,
    }).isRequired,
    memberType: PropTypes.string.isRequired,
    memberExistsIcon: PropTypes.string,
    memberIcon: PropTypes.string.isRequired,
    saveMember: PropTypes.func.isRequired,
    addMember: PropTypes.func.isRequired,
    removeMember: PropTypes.func.isRequired,
    removeMemberBackendErrors: PropTypes.func.isRequired,
    setHasAddedSigner: PropTypes.func.isRequired,
    members: PropTypes.arrayOf(PropTypes.shape({
      memberId: PropTypes.number,
    })),
    memberBackendErrors: PropTypes.arrayOf(PropTypes.shape({})),
    suggestionsList: PropTypes.arrayOf(PropTypes.shape({})),
    handleActionsBeforeNext: PropTypes.func,
    attention: PropTypes.string,
    hasLanguageSensitiveNames: PropTypes.bool,
    hasAddressInputsLanguageSensitiveNames: PropTypes.bool,
    newMemberValidationSchema: PropTypes.shape({}),
    contractMembersValidationSchema: PropTypes.shape({}),
    totalPrice: PropTypes.number,
    isDeviceLgSize: PropTypes.bool.isRequired,
    handleOpenMobileSummary: PropTypes.func.isRequired,
    setStepNavigationBox: PropTypes.func.isRequired,
    isMemberFormOpen: PropTypes.bool.isRequired,
    setIsMemberFormOpen: PropTypes.func.isRequired,
    disabledHasAddedSigner: PropTypes.bool,
    handleResolveSpecificData: PropTypes.func,
    handleWhenHasNoSigner: PropTypes.func,
  }),
  step: PropTypes.shape({}).isRequired,
  unlockedSteps: PropTypes.arrayOf(PropTypes.string).isRequired,
  setUnlockedSteps: PropTypes.func.isRequired,
};

MemberStep.defaultProps = {
  addNewMemberButtonComponent: undefined,
  passportIdInfoComponent: undefined,
  params: {
    members: [],
    memberBackendErrors: null,
    suggestionsList: null,
    attention: null,
    hasLanguageSensitiveNames: false,
    hasAddressInputsLanguageSensitiveNames: false,
    totalPrice: null,
    disabledHasAddedSigner: false,
    memberExistsIcon: undefined,
    handleActionsBeforeNext: () => { },
    newMemberValidationSchema: newMemberSchema,
    contractMembersValidationSchema: contractMembersSchema,
    handleResolveSpecificData: () => { },
    handleWhenHasNotSigner: () => {},
  },
};

export default MemberStep;
